import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Share2,
  Settings,
  X,
  Check,
  Plus,
  Layers,
  Palette,
  Clock,
  Sparkles,
  UserPlus,
  UserMinus,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import MorphingCardStack from "./ui/MorphingCardStack";
import CompilationStack from "./ui/CompilationStack";
import SaveToFavoritesModal from "./SaveToFavoritesModal";
import BackToTopButton from "./BackToTopButton";
import { useUser } from "../hooks/useUser";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { getPrimaryTorrentImageUrl } from "../utils/torrentImages";

// Функция для преобразования данных торрента в формат карточки
const torrentToCard = (torrent) => ({
  id: String(torrent.id),
  title: torrent.title,
  badge: torrent.category || "misc",
  size: torrent.size || "Unknown",
  time: torrent.time || torrent.upload_date || torrent.scraped_at,
  image: getPrimaryTorrentImageUrl(torrent),
  image_urls: Array.isArray(torrent.image_urls) ? torrent.image_urls : [],
  isExclusive: torrent.is_exclusive,
  description: torrent.description || "",
  torrent_path: torrent.torrent_path,
  archive_message_ids: torrent.archive_message_ids,
});

const FAVORITES_PAGE_SIZE = 50;
const COMPILATIONS_PAGE_SIZE = 20;
const COMPILATION_ITEMS_PAGE_SIZE = 50;

const createPagedState = () => ({
  initialized: false,
  hasMore: true,
  nextOffset: 0,
  total: 0,
});

const mergeUniqueById = (prevItems, nextItems) => {
  const seenIds = new Set(prevItems.map((item) => String(item.id)));
  const mergedItems = [...prevItems];

  nextItems.forEach((item) => {
    const itemId = String(item.id);
    if (!seenIds.has(itemId)) {
      seenIds.add(itemId);
      mergedItems.push(item);
    }
  });

  return mergedItems;
};

const removeFromPagedState = (
  state,
  { removedCount = 1, removedLoadedCount = removedCount } = {},
) => {
  const total = Math.max(0, state.total - removedCount);
  const nextOffset = Math.max(0, state.nextOffset - removedLoadedCount);

  return {
    ...state,
    initialized: true,
    total,
    nextOffset,
    hasMore: nextOffset < total,
  };
};

const addToPagedState = (
  state,
  { addedCount = 1, addedLoadedCount = addedCount } = {},
) => {
  const total = state.total + addedCount;
  const nextOffset = state.nextOffset + addedLoadedCount;

  return {
    ...state,
    initialized: true,
    total,
    nextOffset,
    hasMore: nextOffset < total,
  };
};

const compilationToSummary = (compilation) => {
  const previewItems = (
    compilation.preview_items ||
    compilation.items ||
    []
  ).map((item) => ({
    id: String(item.id),
    title: item.title,
    image: item.image_url || item.image,
    image_url: item.image_url || item.image,
  }));

  return {
    id: String(compilation.id),
    title: compilation.title,
    count: parseInt(compilation.items_count || compilation.count || 0, 10),
    items: previewItems,
    preview_items: previewItems,
  };
};

export default function ProfileModal({
  isOpen,
  onClose,
  telegramUser,
  profileUserId = null,
  onCardClick,
  favoriteIds = new Set(), // Set of favorite torrent IDs for current user
  onBookmarkClick, // Callback for adding to favorites
  onOpenProfile, // Callback to open another user's profile in-app
}) {
  const { user } = useUser();
  const scrollContainerRef = useRef(null);
  const outerScrollRef = useRef(null);
  const favoritesLoaderRef = useRef(null);
  const [scrollTarget, setScrollTarget] = useState(null);
  const compilationsLoaderRef = useRef(null);
  const compilationItemsLoaderRef = useRef(null);

  // Определяем, чей профиль просматриваем
  // profileUserId - если передан, смотрим чужой профиль; иначе свой
  const viewingUserId = profileUserId || user?.telegram_id;
  const isOwner =
    !profileUserId || String(profileUserId) === String(user?.telegram_id);

  const [activeTab, setActiveTab] = useState("favorites");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [openedCompilation, setOpenedCompilation] = useState(null);
  const [editingCompilation, setEditingCompilation] = useState(null); // Для редактирования коллекции
  const [editCompilationName, setEditCompilationName] = useState("");
  const [previewItems, setPreviewItems] = useState([]); // Элементы для выбора превью
  const [previewIndex, setPreviewIndex] = useState(0); // Текущий индекс превью
  const [isDragging, setIsDragging] = useState(false);
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const [connectionsTab, setConnectionsTab] = useState("followers");
  const [isFollowingProfile, setIsFollowingProfile] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Реальные данные
  const [favorites, setFavorites] = useState([]);
  const [compilations, setCompilations] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [bookmarkTorrent, setBookmarkTorrent] = useState(null);
  const [loading, setLoading] = useState({
    favorites: false,
    compilations: false,
    compilationItems: false,
    followers: false,
    following: false,
  });
  const [loadingMore, setLoadingMore] = useState({
    favorites: false,
    compilations: false,
    compilationItems: false,
  });
  const [favoritesState, setFavoritesState] = useState(createPagedState());
  const [compilationsState, setCompilationsState] =
    useState(createPagedState());
  const [localFavoriteIds, setLocalFavoriteIds] = useState(
    new Set(favoriteIds),
  ); // Локальное состояние избранного для обновления UI

  const [stats, setStats] = useState({
    followers_count: 0,
    following_count: 0,
    favorites_count: 0,
    compilations_count: 0,
  });

  // Профиль пользователя - загружаем из БД или используем дефолтные значения
  const [profile, setProfile] = useState({
    name: telegramUser?.first_name || telegramUser?.username || "User",
    username: telegramUser?.username || "",
    profile_hash: null,
    status: "Working with 3D and visuals.",
    profession: "CG Artist",
    tags: ["#CG", "#3D", "#DigitalArt"],
    access_granted: false,
  });
  const [editForm, setEditForm] = useState(profile);

  // Загружаем данные профиля из БД при открытии
  useEffect(() => {
    if (isOpen && viewingUserId) {
      setFavorites([]);
      setCompilations([]);
      setOpenedCompilation(null);
      setFavoritesState(createPagedState());
      setCompilationsState(createPagedState());
      setLoadingMore({
        favorites: false,
        compilations: false,
        compilationItems: false,
      });
      fetchProfileData();
    }
  }, [isOpen, viewingUserId]);

  useEffect(() => {
    if (isOpen && viewingUserId && !isOwner && user?.telegram_id) {
      checkFollowStatus();
    }
  }, [isOpen, viewingUserId, isOwner, user?.telegram_id]);

  // Обновляем профиль при изменении telegramUser (только для своего профиля)
  useEffect(() => {
    if (telegramUser && isOwner) {
      setProfile((prev) => ({
        ...prev,
        name: telegramUser.first_name || telegramUser.username || prev.name,
        username: telegramUser.username || prev.username,
      }));
    }
  }, [telegramUser, isOwner]);

  // Синхронизируем локальное избранное с пропсами
  useEffect(() => {
    setLocalFavoriteIds(new Set(favoriteIds));
  }, [favoriteIds]);

  const getAuthHeaders = () => ({
    Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
  });

  // Проверяем, подписаны ли мы на этот профиль
  const checkFollowStatus = async () => {
    try {
      const response = await fetch(
        `/api/users/profile/${viewingUserId}/follow/check?follower_id=${user.telegram_id}`,
        { headers: getAuthHeaders() },
      );
      if (response.ok) {
        const data = await response.json();
        setIsFollowingProfile(data.is_following);
      }
    } catch (error) {
      console.error("Error checking follow status:", error);
    }
  };

  const fetchProfileData = async () => {
    try {
      const response = await fetch(`/api/users/profile/${viewingUserId}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.profile) {
          setProfile((prev) => ({
            ...prev,
            name: data.profile.first_name || data.profile.username || prev.name,
            username: data.profile.username || prev.username,
            profile_hash: data.profile.profile_hash || prev.profile_hash,
            status: data.profile.status || prev.status,
            profession: data.profile.profession || prev.profession,
            tags: data.profile.tags || prev.tags,
            access_granted: data.profile.access_granted || false,
            photo_url: data.profile.photo_url || null,
          }));
          setStats({
            followers_count: data.profile.followers_count || 0,
            following_count: data.profile.following_count || 0,
            favorites_count: data.profile.favorites_count || 0,
            compilations_count: data.profile.compilations_count || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchFavorites = async ({ reset = false } = {}) => {
    if (!viewingUserId) return;

    const offset = reset ? 0 : favoritesState.nextOffset;
    if (
      !reset &&
      (!favoritesState.hasMore || loading.favorites || loadingMore.favorites)
    ) {
      return;
    }

    if (reset) {
      setLoading((prev) => ({ ...prev, favorites: true }));
    } else {
      setLoadingMore((prev) => ({ ...prev, favorites: true }));
    }

    try {
      const query = new URLSearchParams({
        limit: String(FAVORITES_PAGE_SIZE),
        offset: String(offset),
      });
      const response = await fetch(
        `/api/users/profile/${viewingUserId}/favorites?${query.toString()}`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        const data = await response.json();
        const cards = (data.favorites || []).map(torrentToCard);
        setFavorites((prev) => (reset ? cards : mergeUniqueById(prev, cards)));
        setFavoritesState({
          initialized: true,
          hasMore: Boolean(data.hasMore),
          nextOffset: offset + cards.length,
          total: parseInt(data.total || 0, 10),
        });
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      if (reset) {
        setLoading((prev) => ({ ...prev, favorites: false }));
      } else {
        setLoadingMore((prev) => ({ ...prev, favorites: false }));
      }
    }
  };

  const fetchCompilations = async ({ reset = false } = {}) => {
    if (!viewingUserId) return;

    const offset = reset ? 0 : compilationsState.nextOffset;
    if (
      !reset &&
      (!compilationsState.hasMore ||
        loading.compilations ||
        loadingMore.compilations)
    ) {
      return;
    }

    if (reset) {
      setLoading((prev) => ({ ...prev, compilations: true }));
    } else {
      setLoadingMore((prev) => ({ ...prev, compilations: true }));
    }

    try {
      const query = new URLSearchParams({
        limit: String(COMPILATIONS_PAGE_SIZE),
        offset: String(offset),
      });
      const response = await fetch(
        `/api/users/profile/${viewingUserId}/compilations?${query.toString()}`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        const data = await response.json();
        const formattedCompilations = (data.compilations || []).map(
          compilationToSummary,
        );
        setCompilations((prev) =>
          reset
            ? formattedCompilations
            : mergeUniqueById(prev, formattedCompilations),
        );
        setCompilationsState({
          initialized: true,
          hasMore: Boolean(data.hasMore),
          nextOffset: offset + formattedCompilations.length,
          total: parseInt(data.total || 0, 10),
        });
      }
    } catch (error) {
      console.error("Error fetching compilations:", error);
    } finally {
      if (reset) {
        setLoading((prev) => ({ ...prev, compilations: false }));
      } else {
        setLoadingMore((prev) => ({ ...prev, compilations: false }));
      }
    }
  };

  const fetchCompilationItems = async (
    compilation,
    { reset = false, loadAll = false } = {},
  ) => {
    const targetCompilation = compilation || openedCompilation;
    if (!viewingUserId || !targetCompilation) {
      return null;
    }

    const offset = loadAll ? 0 : reset ? 0 : targetCompilation.nextOffset || 0;
    if (
      !loadAll &&
      !reset &&
      (!targetCompilation.hasMore ||
        loading.compilationItems ||
        loadingMore.compilationItems)
    ) {
      return null;
    }

    if (!loadAll) {
      if (reset) {
        setLoading((prev) => ({ ...prev, compilationItems: true }));
      } else {
        setLoadingMore((prev) => ({ ...prev, compilationItems: true }));
      }
    }

    try {
      const query = new URLSearchParams(
        loadAll
          ? { all: "true" }
          : {
              limit: String(COMPILATION_ITEMS_PAGE_SIZE),
              offset: String(offset),
            },
      );
      const response = await fetch(
        `/api/users/profile/${viewingUserId}/compilations/${targetCompilation.id}?${query.toString()}`,
        { headers: getAuthHeaders() },
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (loadAll) {
        return data;
      }

      const cards = (data.items || []).map(torrentToCard);
      setOpenedCompilation((prev) => {
        if (!prev || String(prev.id) !== String(targetCompilation.id)) {
          return prev;
        }

        return {
          ...prev,
          items: reset ? cards : mergeUniqueById(prev.items || [], cards),
          count: parseInt(data.total || prev.count || 0, 10),
          hasMore: Boolean(data.hasMore),
          nextOffset: offset + cards.length,
          initialized: true,
        };
      });

      return data;
    } catch (error) {
      console.error("Error loading compilation:", error);
      if (!loadAll) {
        showErrorToast("Error loading collection");
      }
      return null;
    } finally {
      if (!loadAll) {
        if (reset) {
          setLoading((prev) => ({ ...prev, compilationItems: false }));
        } else {
          setLoadingMore((prev) => ({ ...prev, compilationItems: false }));
        }
      }
    }
  };

  useEffect(() => {
    if (!isOpen || !viewingUserId) {
      return;
    }

    if (activeTab === "favorites" && !favoritesState.initialized) {
      fetchFavorites({ reset: true });
    }

    if (activeTab === "compilation" && !compilationsState.initialized) {
      fetchCompilations({ reset: true });
    }
  }, [
    isOpen,
    viewingUserId,
    activeTab,
    favoritesState.initialized,
    compilationsState.initialized,
  ]);

  // Pick the element that actually scrolls (outer on mobile, inner on desktop)
  useEffect(() => {
    if (!isOpen) {
      setScrollTarget(null);
      return undefined;
    }

    const updateScrollTarget = () => {
      const inner = scrollContainerRef.current;
      const outer = outerScrollRef.current;
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

      if (isDesktop && inner) {
        setScrollTarget(inner);
        return;
      }

      setScrollTarget(outer || inner);
    };

    updateScrollTarget();
    // Re-check after content paints / tab switches
    const raf = requestAnimationFrame(updateScrollTarget);
    window.addEventListener("resize", updateScrollTarget);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateScrollTarget);
    };
  }, [
    isOpen,
    activeTab,
    favorites.length,
    compilations.length,
    openedCompilation,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let target = null;
    let loadMore = null;

    if (activeTab === "favorites") {
      target = favoritesLoaderRef.current;
      loadMore = () => fetchFavorites();
    } else if (activeTab === "compilation" && openedCompilation) {
      target = compilationItemsLoaderRef.current;
      loadMore = () => fetchCompilationItems();
    } else if (activeTab === "compilation") {
      target = compilationsLoaderRef.current;
      loadMore = () => fetchCompilations();
    }

    if (!target || !loadMore) {
      return undefined;
    }

    const scrollContainer = scrollContainerRef.current;
    const scrollContainerStyle = scrollContainer
      ? window.getComputedStyle(scrollContainer)
      : null;
    const hasScrollableRoot = Boolean(
      scrollContainer &&
      scrollContainerStyle &&
      ["auto", "scroll", "overlay"].includes(scrollContainerStyle.overflowY) &&
      scrollContainer.scrollHeight > scrollContainer.clientHeight + 1,
    );

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      {
        root: hasScrollableRoot ? scrollContainer : null,
        threshold: 0.25,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    isOpen,
    activeTab,
    openedCompilation?.id,
    openedCompilation?.nextOffset,
    favoritesState.nextOffset,
    compilationsState.nextOffset,
    loading.favorites,
    loading.compilations,
    loading.compilationItems,
    loadingMore.favorites,
    loadingMore.compilations,
    loadingMore.compilationItems,
  ]);

  const fetchFollowers = async () => {
    setLoading((prev) => ({ ...prev, followers: true }));
    try {
      const response = await fetch(
        `/api/users/profile/${viewingUserId}/followers`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setFollowers(data.followers || []);
      }
    } catch (error) {
      console.error("Error fetching followers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, followers: false }));
    }
  };

  const fetchFollowing = async () => {
    setLoading((prev) => ({ ...prev, following: true }));
    try {
      const response = await fetch(
        `/api/users/profile/${viewingUserId}/following`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setFollowing(data.following || []);
      }
    } catch (error) {
      console.error("Error fetching following:", error);
    } finally {
      setLoading((prev) => ({ ...prev, following: false }));
    }
  };

  // Загружаем followers/following при открытии модала connections
  useEffect(() => {
    if (connectionsOpen && user?.telegram_id) {
      fetchFollowers();
      fetchFollowing();
    }
  }, [connectionsOpen, user?.telegram_id]);

  const handleFollow = async (targetUserId) => {
    try {
      const response = await fetch(
        `/api/users/profile/${targetUserId}/follow`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        },
      );
      if (response.ok) {
        // Обновляем локальное состояние
        setFollowers((prev) =>
          prev.map((f) =>
            f.telegram_id === targetUserId ? { ...f, is_following: true } : f,
          ),
        );
        setFollowing((prev) =>
          prev.map((f) =>
            f.telegram_id === targetUserId ? { ...f, is_following: true } : f,
          ),
        );
        showSuccessToast("Subscribed");
      }
    } catch (error) {
      console.error("Error following user:", error);
      showErrorToast("Error subscribing");
    }
  };

  const handleUnfollow = async (targetUserId) => {
    try {
      const response = await fetch(
        `/api/users/profile/${targetUserId}/follow`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        // Обновляем локальное состояние в списках
        setFollowers((prev) =>
          prev.map((f) =>
            f.telegram_id === targetUserId ? { ...f, is_following: false } : f,
          ),
        );
        setFollowing((prev) =>
          prev.map((f) =>
            f.telegram_id === targetUserId ? { ...f, is_following: false } : f,
          ),
        );
        // Не обновляем счётчики просматриваемого профиля - это изменяет только NAШИ подписки
        showSuccessToast("Unsubscribed");
      }
    } catch (error) {
      console.error("Error unfollowing user:", error);
      showErrorToast("Error unsubscribing");
    }
  };

  // Открыть модальное окно добавления в избранное
  const openBookmarkModal = (card) => {
    setBookmarkTorrent({
      ...card,
      id: Number(card.id),
      image_url: card.image, // Map image prop to image_url for Preview
      image_urls: card.image_urls,
    });
  };

  const handleDataRefresh = (payload) => {
    if (!isOwner) {
      return;
    }

    if (payload?.type === "favorite-added") {
      if (payload.prependToFavorites) {
        const newCard = torrentToCard(payload.torrent);
        setFavorites((prev) => [
          newCard,
          ...prev.filter((card) => card.id !== newCard.id),
        ]);
        setFavoritesState((prev) =>
          prev.initialized ? addToPagedState(prev) : prev,
        );
      }
      fetchProfileData();
      return;
    }

    if (payload?.type === "favorite-removed") {
      const removedId = String(payload.torrentId);
      const wasLoaded = favorites.some((card) => card.id === removedId);
      const deletedCompilationIds = new Set(
        payload.deletedCompilationIds || [],
      );

      setFavorites((prev) => prev.filter((card) => card.id !== removedId));
      setFavoritesState((prev) =>
        removeFromPagedState(prev, {
          removedLoadedCount: wasLoaded ? 1 : 0,
        }),
      );

      if (activeTab === "compilation") {
        if (openedCompilation) {
          if (deletedCompilationIds.has(String(openedCompilation.id))) {
            setOpenedCompilation(null);
            if (compilationsState.initialized) {
              fetchCompilations({ reset: true });
            }
          } else {
            fetchCompilationItems(openedCompilation, { reset: true });
          }
        } else if (compilationsState.initialized) {
          fetchCompilations({ reset: true });
        }
      } else {
        setCompilationsState((prev) => ({ ...prev, initialized: false }));
      }

      fetchProfileData();
      return;
    }

    if (
      payload?.type === "compilation-item-added" ||
      payload?.type === "compilation-item-removed" ||
      payload?.type === "compilation-created"
    ) {
      if (payload.type === "compilation-created" && payload.compilation) {
        const createdCompilation = compilationToSummary(payload.compilation);
        setCompilations((prev) =>
          compilationsState.initialized
            ? [
                createdCompilation,
                ...prev.filter((item) => item.id !== createdCompilation.id),
              ]
            : prev,
        );
        setCompilationsState((prev) =>
          prev.initialized ? addToPagedState(prev) : prev,
        );
      }

      if (activeTab === "compilation") {
        if (
          openedCompilation &&
          payload.compilationId &&
          String(payload.compilationId) === String(openedCompilation.id)
        ) {
          if (payload.compilationDeleted) {
            setOpenedCompilation(null);
          } else {
            fetchCompilationItems(openedCompilation, { reset: true });
          }
        } else if (!openedCompilation && compilationsState.initialized) {
          fetchCompilations({ reset: true });
        }
      } else {
        setCompilationsState((prev) => ({ ...prev, initialized: false }));
      }

      fetchProfileData();
    }
  };

  // Обработчик изменения избранного из модального окна
  const handleLocalFavoriteChange = (id, isFav) => {
    setLocalFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.add(Number(id));
      else next.delete(Number(id));
      return next;
    });
  };

  // Открыть компиляцию и загрузить все её торренты
  const handleOpenCompilation = async (compilation) => {
    const nextCompilation = {
      ...compilation,
      id: String(compilation.id),
      items: [],
      count: parseInt(compilation.count || 0, 10),
      hasMore: true,
      nextOffset: 0,
      initialized: false,
    };

    setOpenedCompilation(nextCompilation);
    await fetchCompilationItems(nextCompilation, { reset: true });
  };

  // Открыть модал редактирования коллекции
  const handleEditCompilation = async (compilation, e) => {
    e?.stopPropagation();
    setEditCompilationName(compilation.title);

    // Загружаем полные данные коллекции для выбора превью
    try {
      const data = await fetchCompilationItems(compilation, { loadAll: true });
      if (data?.success) {
        const items = (data.items || []).map((item) => ({
          id: item.id,
          image: item.image_url,
          title: item.title,
        }));
        setPreviewItems(items);
        setPreviewIndex(0);
        setEditingCompilation({
          ...compilation,
          items: items,
          count: items.length,
        });
      } else {
        // Fallback к preview_items
        const items = (compilation.preview_items || []).map((item) => ({
          id: item.id,
          image: item.image_url,
          title: item.title,
        }));
        setPreviewItems(items);
        setPreviewIndex(0);
        setEditingCompilation(compilation);
      }
    } catch (error) {
      console.error("Error loading compilation for edit:", error);
      const items = (compilation.preview_items || []).map((item) => ({
        id: item.id,
        image: item.image_url,
        title: item.title,
      }));
      setPreviewItems(items);
      setPreviewIndex(0);
      setEditingCompilation(compilation);
    }
  };

  // Обработка swipe для выбора превью
  const handlePreviewDragEnd = (event, info) => {
    const SWIPE_THRESHOLD = 50;
    const { offset, velocity } = info;
    const swipe = Math.abs(offset.x) * velocity.x;

    if (offset.x < -SWIPE_THRESHOLD || swipe < -1000) {
      // Свайп влево - следующая карточка
      setPreviewIndex((prev) => (prev + 1) % previewItems.length);
    } else if (offset.x > SWIPE_THRESHOLD || swipe > 1000) {
      // Свайп вправо - предыдущая карточка
      setPreviewIndex(
        (prev) => (prev - 1 + previewItems.length) % previewItems.length,
      );
    }
    setIsDragging(false);
  };

  // Получить порядок карточек для стека (показываем все, но визуально ограничиваем смещение)
  const MAX_RENDER_CARDS = 5; // Оптимизация: рендерим только 5 карточек для производительности

  const getStackOrder = () => {
    const reordered = [];
    // Ограничиваем количество рендеримых карточек
    const visibleCount = Math.min(MAX_RENDER_CARDS, previewItems.length);

    for (let i = 0; i < visibleCount; i++) {
      const index = (previewIndex + i) % previewItems.length;
      reordered.push({ ...previewItems[index], stackPosition: i });
    }

    return reordered.reverse();
  };

  // Сохранить изменения коллекции (название + порядок превью)
  const handleSaveCompilationChanges = async () => {
    if (!editingCompilation || !editCompilationName.trim()) return;

    try {
      // Сохраняем название
      const titleResponse = await fetch(
        `/api/users/profile/${user.telegram_id}/compilations/${editingCompilation.id}`,
        {
          method: "PUT",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ title: editCompilationName.trim() }),
        },
      );

      // Сохраняем порядок превью (новый порядок начиная с previewIndex)
      if (previewItems.length > 1) {
        const newOrder = [];
        for (let i = 0; i < previewItems.length; i++) {
          const index = (previewIndex + i) % previewItems.length;
          newOrder.push(previewItems[index].id);
        }

        await fetch(
          `/api/users/profile/${user.telegram_id}/compilations/${editingCompilation.id}/reorder`,
          {
            method: "POST",
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ item_ids: newOrder }),
          },
        );
      }

      if (titleResponse.ok) {
        // Обновляем локально с новым превью
        const newPreviewItems = [];
        for (let i = 0; i < Math.min(4, previewItems.length); i++) {
          const index = (previewIndex + i) % previewItems.length;
          newPreviewItems.push(previewItems[index]);
        }

        setCompilations((prev) =>
          prev.map((c) =>
            c.id === editingCompilation.id
              ? {
                  ...c,
                  title: editCompilationName.trim(),
                  items: newPreviewItems.map((item) => ({
                    id: String(item.id),
                    title: item.title,
                    image: item.image,
                  })),
                }
              : c,
          ),
        );
        showSuccessToast("Collection updated");
        setEditingCompilation(null);
        setPreviewItems([]);
        setPreviewIndex(0);
      }
    } catch (error) {
      console.error("Error saving compilation:", error);
      showErrorToast("Error saving");
    }
  };

  // Удалить коллекцию
  const handleDeleteCompilation = async () => {
    if (!editingCompilation) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this collection?",
    );
    if (!confirmed) return;

    try {
      const response = await fetch(
        `/api/users/profile/${user.telegram_id}/compilations/${editingCompilation.id}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );

      if (response.ok) {
        setCompilations((prev) =>
          prev.filter((c) => c.id !== editingCompilation.id),
        );
        setCompilationsState((prev) => removeFromPagedState(prev));
        fetchProfileData();
        showSuccessToast("Collection deleted");
        setEditingCompilation(null);
      }
    } catch (error) {
      console.error("Error deleting compilation:", error);
      showErrorToast("Error deleting");
    }
  };

  const handleOpenSettings = () => {
    setEditForm(profile);
    setTagInput("");
    setIsSettingsOpen(true);
  };

  const handleSaveSettings = async () => {
    try {
      const response = await fetch(`/api/users/profile/${user.telegram_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        },
        body: JSON.stringify({
          status: editForm.status,
          profession: editForm.profession,
          tags: editForm.tags,
        }),
      });

      if (response.ok) {
        setProfile(editForm);
      }
    } catch (error) {
      console.error("Error saving profile:", error);
    }

    setIsSettingsOpen(false);
  };

  const handleAddTag = () => {
    const cleanedTag = tagInput.trim().replace(/\s+/g, "");
    if (cleanedTag) {
      let newTag = cleanedTag;
      if (!newTag.startsWith("#")) {
        newTag = "#" + newTag;
      }
      setEditForm({ ...editForm, tags: [...editForm.tags, newTag] });
      setTagInput("");
    }
  };

  const handleRemoveTag = (index) => {
    const newTags = editForm.tags.filter((_, i) => i !== index);
    setEditForm({ ...editForm, tags: newTags });
  };

  // Подписаться/отписаться от просматриваемого профиля
  const handleFollowProfile = async () => {
    if (followLoading || isOwner) return;
    setFollowLoading(true);

    try {
      const method = isFollowingProfile ? "DELETE" : "POST";
      const response = await fetch(
        `/api/users/profile/${viewingUserId}/follow`,
        {
          method,
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        setIsFollowingProfile(!isFollowingProfile);
        // Обновляем счётчик подписчиков
        setStats((prev) => ({
          ...prev,
          followers_count: isFollowingProfile
            ? Math.max(0, prev.followers_count - 1)
            : prev.followers_count + 1,
        }));
        showSuccessToast(isFollowingProfile ? "Unfollowed" : "Following");
      }
    } catch (error) {
      console.error("Error following/unfollowing:", error);
      showErrorToast("Error");
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShare = async () => {
    // Генерируем анонимную ссылку для Telegram Mini App (без telegram_id)
    const botUsername = "CGPEERSSITE_BOT";
    const appName = "Cgpeersapp";
    // Используем profile_hash для анонимности, фоллбэк на user.profile_hash
    const hashId = profile.profile_hash || user?.profile_hash;
    if (!hashId) {
      showErrorToast("Profile link not available yet, try again later");
      return;
    }
    const profileUrl = `https://t.me/${botUsername}/${appName}?startapp=profile_${hashId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.name}'s Profile`,
          text: "Check out this profile on CGPeers!",
          url: profileUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.log("Error sharing:", err);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(profileUrl);
        showSuccessToast("Profile link copied!");
      } catch (err) {
        console.log("Error copying to clipboard:", err);
        showErrorToast("Failed to copy link");
      }
    }
  };

  if (!isOpen) return null;

  // Определяем, есть ли у просматриваемого пользователя премиум
  const isPremium = isOwner
    ? user?.access_granted === true
    : profile.access_granted === true;

  // Аватар и username - для своего профиля из telegramUser, для чужого из загруженных данных profile
  const avatarUrl = isOwner
    ? telegramUser?.photo_url || "/default-avatar.png"
    : profile.photo_url || "/default-avatar.png";
  const displayUsername = profile.username
    ? `@${profile.username}`
    : `@user${viewingUserId || ""}`;

  return (
    <div
      ref={outerScrollRef}
      className="fixed inset-0 z-[60] flex items-start lg:items-center justify-center overflow-y-auto lg:bg-black/50 lg:backdrop-blur-sm"
    >
      {/* Фон на весь экран, пока кабинет не стал модальным окном */}
      <div
        className="fixed inset-0 -z-10 lg:hidden"
        style={{
          background: `
            radial-gradient(
              ellipse at 80% 40%,
              rgba(74, 48, 128, 0.18) 0%,
              rgba(74, 48, 128, 0.09) 30%,
              rgba(40, 20, 80, 0.04) 60%,
              transparent 80%
            ),
            linear-gradient(
              to bottom,
              #0f0f0f 0%,
              #141418 45%,
              #1a1a20 100%
            )`,
        }}
      />

      <div
        ref={scrollContainerRef}
        className="w-full lg:max-w-3xl min-h-screen lg:min-h-0 lg:h-[95vh] lg:max-h-[95vh] lg:my-4 lg:rounded-2xl lg:overflow-hidden lg:shadow-2xl lg:border lg:border-gray-800 text-white font-sans relative lg:overflow-y-auto"
        style={{
          backgroundColor: "#141418",
        }}
      >
        {/* Header with background image */}
        <div className="relative h-96 md:h-96 lg:h-96 overflow-hidden">
          {/* Profile image as background */}
          <img
            src={avatarUrl}
            alt="Profile background"
            className="absolute inset-0 w-full h-full object-cover"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default-avatar.png";
            }}
            style={{
              maskImage:
                "linear-gradient(to bottom, black 50%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, black 50%, transparent 100%)",
            }}
          />

          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 z-10">
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <button
              onClick={handleShare}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <Share2 size={20} />
            </button>
          </div>
        </div>

        {/* Profile content */}
        <div className="relative -mt-12 px-5 md:px-8 lg:px-10 pb-6">
          {/* Profile name and Settings */}
          <div className="mb-1 flex items-center justify-between">
            <h1 className="flex items-center gap-2 text-xl md:text-2xl font-bold tracking-tight text-white">
              {profile.name}
              {/* Premium Badge - показываем только если isPremium */}
              {isPremium && (
                <div
                  className="relative flex items-center justify-center"
                  title="Premium Verification"
                >
                  <div className="absolute inset-0 rounded-full bg-yellow-500/20 blur-md"></div>
                  <div
                    className="h-6 w-6 animate-aurora bg-size-[200%_auto]"
                    style={{
                      backgroundImage: `linear-gradient(to right, #FF0080, #7928CA, #FF4D4D, #F9CB28, #0070F3, #38bdf8, #A020F0, #FF0080)`,
                      maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M10.95 12.7L9.5 11.275Q9.225 11 8.813 11t-.713.3q-.275.275-.275.7t.275.7l2.15 2.15q.3.3.7.3t.7-.3l4.25-4.25q.3-.3.287-.7t-.287-.7q-.3-.3-.712-.312t-.713.287zm-2.8 9.05L6.7 19.3l-2.75-.6q-.375-.075-.6-.387t-.175-.688L3.45 14.8l-1.875-2.15q-.25-.275-.25-.65t.25-.65L3.45 9.2l-.275-2.825q-.05-.375.175-.688t.6-.387l2.75-.6l1.45-2.45q.2-.325.55-.438t.7.038l2.6 1.1l2.6-1.1q.35-.15.7-.038t.55.438L17.3 4.7l2.75.6q.375.075.6.388t.175.687L20.55 9.2l1.875 2.15q.25.275.25.65t-.25.65L20.55 14.8l.275 2.825q.05.375-.175.688t-.6.387l-2.75.6l-1.45 2.45q-.2.325-.55.438t-.7-.038l-2.6-1.1l-2.6 1.1q-.35.15-.7.038t-.55-.438'/%3E%3C/svg%3E")`,
                      WebkitMaskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M10.95 12.7L9.5 11.275Q9.225 11 8.813 11t-.713.3q-.275.275-.275.7t.275.7l2.15 2.15q.3.3.7.3t.7-.3l4.25-4.25q.3-.3.287-.7t-.287-.7q-.3-.3-.712-.312t-.713.287zm-2.8 9.05L6.7 19.3l-2.75-.6q-.375-.075-.6-.387t-.175-.688L3.45 14.8l-1.875-2.15q-.25-.275-.25-.65t.25-.65L3.45 9.2l-.275-2.825q-.05-.375.175-.688t.6-.387l2.75-.6l1.45-2.45q.2-.325.55-.438t.7.038l2.6 1.1l2.6-1.1q.35-.15.7-.038t.55.438L17.3 4.7l2.75.6q.375.075.6.388t.175.687L20.55 9.2l1.875 2.15q.25.275.25.65t-.25.65L20.55 14.8l.275 2.825q.05.375-.175.688t-.6.387l-2.75.6l-1.45 2.45q-.2.325-.55.438t-.7-.038l-2.6-1.1l-2.6 1.1q-.35.15-.7.038t-.55-.438'/%3E%3C/svg%3E")`,
                      maskSize: "contain",
                      WebkitMaskSize: "contain",
                      maskRepeat: "no-repeat",
                      WebkitMaskRepeat: "no-repeat",
                      maskPosition: "center",
                      WebkitMaskPosition: "center",
                      animationDuration: "3s",
                    }}
                  />
                </div>
              )}
            </h1>
            <div className="flex items-center gap-2">
              {/* Follow button - показываем только для чужих профилей */}
              {!isOwner && (
                <button
                  onClick={handleFollowProfile}
                  disabled={followLoading}
                  className={`h-9 px-4 rounded-full flex items-center justify-center gap-2 border transition-colors ${
                    isFollowingProfile
                      ? "bg-white/5 border-gray-600 text-gray-300 hover:text-white hover:border-gray-500"
                      : "bg-orange-500/20 border-orange-400/40 text-orange-200 hover:text-white hover:border-orange-300"
                  }`}
                  aria-label={isFollowingProfile ? "Unfollow" : "Follow"}
                >
                  {followLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : isFollowingProfile ? (
                    <>
                      <UserMinus size={16} />
                      <span className="text-sm">Following</span>
                    </>
                  ) : (
                    <>
                      <UserPlus size={16} />
                      <span className="text-sm">Follow</span>
                    </>
                  )}
                </button>
              )}
              {/* Settings button - показываем только для своего профиля */}
              {isOwner && (
                <button
                  onClick={handleOpenSettings}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <Settings size={20} />
                </button>
              )}
            </div>
          </div>

          <p className="text-gray-400 text-xs md:text-sm mb-5">
            {displayUsername}
          </p>

          {/* Stats */}
          <div className="flex items-center justify-between mb-6 text-center bg-white/[0.02] md:bg-white/[0.03] rounded-xl md:rounded-2xl py-3 md:py-4 px-2">
            <button
              onClick={() => {
                setConnectionsTab("followers");
                setConnectionsOpen(true);
              }}
              className="flex-1 hover:text-white transition-colors"
            >
              <div className="text-xl md:text-2xl font-bold">
                {stats.followers_count}
              </div>
              <div className="text-xs md:text-sm text-gray-400">Followers</div>
            </button>
            <div className="w-px h-10 bg-gray-700/50"></div>
            <button
              onClick={() => {
                setConnectionsTab("following");
                setConnectionsOpen(true);
              }}
              className="flex-1 hover:text-white transition-colors"
            >
              <div className="text-xl md:text-2xl font-bold">
                {stats.following_count}
              </div>
              <div className="text-xs md:text-sm text-gray-400">Following</div>
            </button>
            <div className="w-px h-10 bg-gray-700/50"></div>
            <div className="flex-1">
              <div className="text-xl md:text-2xl font-bold">
                {stats.favorites_count}
              </div>
              <div className="text-xs md:text-sm text-gray-400">Favorites</div>
            </div>
            <div className="w-px h-10 bg-gray-700/50"></div>
            <div className="flex-1">
              <div className="text-xl md:text-2xl font-bold">
                {stats.compilations_count}
              </div>
              <div className="text-xs md:text-sm text-gray-400">
                Collections
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="mb-6">
            <p className="text-lg md:text-xl font-semibold mb-2">
              {profile.status}
            </p>
            <p className="text-sm md:text-base text-gray-300 mb-3">
              {profile.profession}
            </p>
            <div className="flex flex-wrap gap-2">
              {profile.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 rounded-full bg-gray-800 text-xs md:text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="sticky top-0 z-50 flex justify-center gap-8 md:gap-12 -mx-5 md:-mx-8 lg:-mx-10 px-5 md:px-8 lg:px-10 pt-2 bg-black/0 backdrop-blur-lg">
            <button
              onClick={() => {
                setActiveTab("favorites");
                setOpenedCompilation(null);
              }}
              className={`pb-3 text-sm md:text-base font-medium relative transition-colors ${
                activeTab === "favorites" ? "text-orange-400" : "text-gray-500"
              }`}
            >
              FAVORITES
              {activeTab === "favorites" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-500 rounded-full"></div>
              )}
            </button>
            <button
              onClick={() => {
                setActiveTab("compilation");
                setOpenedCompilation(null);
              }}
              className={`pb-3 text-sm md:text-base font-medium relative transition-colors ${
                activeTab === "compilation"
                  ? "text-orange-400"
                  : "text-gray-500"
              }`}
            >
              COLLECTIONS
              {activeTab === "compilation" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-500 rounded-full"></div>
              )}
            </button>
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-700/70 to-transparent"></div>
          </div>

          {/* Content - FAVORITES Tab */}
          {activeTab === "favorites" && (
            <div className="pt-5 pb-8">
              {loading.favorites ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : favorites.length > 0 ? (
                <>
                  <MorphingCardStack
                    cards={favorites}
                    favoriteIds={localFavoriteIds}
                    onBookmarkClick={openBookmarkModal}
                    onCardClick={onCardClick}
                  />
                  {(favoritesState.hasMore || loadingMore.favorites) && (
                    <div
                      ref={favoritesLoaderRef}
                      className="flex items-center justify-center py-6"
                    >
                      <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p className="mb-2">No favorite torrents</p>
                  <p className="text-sm">
                    Add torrents to favorites by clicking the bookmark
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Content - COMPILATION Tab */}
          {activeTab === "compilation" && (
            <div className="pt-5 pb-8">
              {loading.compilations ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : !openedCompilation ? (
                compilations.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {compilations.map((compilation) => (
                        <CompilationStack
                          key={compilation.id}
                          title={compilation.title}
                          count={compilation.count}
                          items={compilation.items}
                          onClick={() => handleOpenCompilation(compilation)}
                          onEdit={(e) => handleEditCompilation(compilation, e)}
                          isOwner={isOwner}
                        />
                      ))}
                    </div>
                    {(compilationsState.hasMore ||
                      loadingMore.compilations) && (
                      <div
                        ref={compilationsLoaderRef}
                        className="flex items-center justify-center py-6"
                      >
                        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p className="mb-2">No collections</p>
                    <p className="text-sm">
                      Create collections when saving torrents
                    </p>
                  </div>
                )
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={() => setOpenedCompilation(null)}
                      className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 border border-gray-800 text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <ArrowLeft size={16} />
                    </button>
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {openedCompilation.title}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {openedCompilation.count} items
                      </p>
                    </div>
                  </div>
                  <MorphingCardStack
                    cards={openedCompilation.items}
                    favoriteIds={localFavoriteIds}
                    onBookmarkClick={openBookmarkModal}
                    onCardClick={onCardClick}
                  />
                  {(openedCompilation.hasMore ||
                    loadingMore.compilationItems) && (
                    <div
                      ref={compilationItemsLoaderRef}
                      className="flex items-center justify-center py-6"
                    >
                      <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Followers/Following Modal */}
        {connectionsOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div
              className="w-full max-w-sm md:max-w-md bg-[#141418] rounded-2xl p-5 md:p-6 border border-gray-800 shadow-2xl"
              style={{
                background: "linear-gradient(to bottom, #16161c, #0f0f0f)",
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-white">
                    Connections
                  </h2>
                  <p className="text-xs text-gray-500">
                    People linked to this profile
                  </p>
                </div>
                <button
                  onClick={() => setConnectionsOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex items-center gap-2 mb-4 bg-gray-900/60 border border-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setConnectionsTab("followers")}
                  className={`flex-1 text-xs font-medium py-2 rounded-md transition-colors ${
                    connectionsTab === "followers"
                      ? "bg-gray-700 text-white"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  Followers ({stats.followers_count})
                </button>
                <button
                  onClick={() => setConnectionsTab("following")}
                  className={`flex-1 text-xs font-medium py-2 rounded-md transition-colors ${
                    connectionsTab === "following"
                      ? "bg-gray-700 text-white"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  Following ({stats.following_count})
                </button>
              </div>

              <div className="space-y-3 max-h-[360px] md:max-h-[450px] overflow-y-auto pr-1">
                {loading.followers || loading.following ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (connectionsTab === "followers" ? followers : following)
                    .length > 0 ? (
                  (connectionsTab === "followers" ? followers : following).map(
                    (person) => (
                      <div
                        key={person.telegram_id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-gray-900/60 border border-gray-800 hover:border-gray-700 transition-colors"
                      >
                        <div className="relative">
                          {person.photo_url ? (
                            <img
                              src={person.photo_url}
                              alt={person.username || "User"}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                              {(person.username || "U")[0].toUpperCase()}
                            </div>
                          )}
                          {person.access_granted && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full border-2 border-gray-900" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">
                            {person.first_name ||
                              person.username ||
                              `User ${person.telegram_id}`}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            @{person.username || person.telegram_id}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* View Profile Button */}
                          <button
                            onClick={() => {
                              setConnectionsOpen(false);
                              // Открываем профиль этого пользователя внутри приложения
                              if (onOpenProfile) {
                                // Передаём hash для анонимности, или ID как фоллбэк
                                onOpenProfile(
                                  person.profile_hash ||
                                    String(person.telegram_id),
                                );
                              }
                            }}
                            className="p-2 rounded-full bg-gray-800/50 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600 transition-colors"
                            title="View Profile"
                          >
                            <Eye size={14} />
                          </button>
                          {/* Follow/Unfollow Button */}
                          <button
                            onClick={() =>
                              person.is_following
                                ? handleUnfollow(person.telegram_id)
                                : handleFollow(person.telegram_id)
                            }
                            className={`p-2 rounded-full border transition-colors ${
                              person.is_following
                                ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300 hover:text-white hover:border-emerald-300"
                                : "bg-orange-500/20 border-orange-400/40 text-orange-300 hover:text-white hover:border-orange-300"
                            }`}
                            title={person.is_following ? "Unfollow" : "Follow"}
                          >
                            {person.is_following ? (
                              <UserMinus size={14} />
                            ) : (
                              <UserPlus size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>
                      {connectionsTab === "followers"
                        ? "No followers"
                        : "No following"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Collection Modal */}
        {editingCompilation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div
              className="w-full max-w-sm md:max-w-md bg-[#1a1a20] rounded-2xl p-6 border border-gray-800 shadow-xl"
              style={{
                background: `linear-gradient(to bottom, #1a1a20, #0f0f0f)`,
              }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">
                  Edit Collection
                </h2>
                <button
                  onClick={() => {
                    setEditingCompilation(null);
                    setPreviewItems([]);
                    setPreviewIndex(0);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Preview Selection Label */}
              <div className="text-center mb-2">
                <p className="text-sm text-white font-medium">Choose Preview</p>
                <p className="text-xs text-gray-500">Swipe left or right</p>
              </div>

              {/* Swipeable Preview Stack */}
              <div className="flex justify-center mb-4">
                <div className="relative w-36 h-36">
                  <AnimatePresence mode="popLayout">
                    {previewItems.length > 0 &&
                      getStackOrder().map((item) => {
                        const isTopCard = item.stackPosition === 0;
                        return (
                          <motion.div
                            key={item.id}
                            layoutId={`preview-${item.id}`}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{
                              opacity: 1,
                              scale: 1,
                              y: Math.min(item.stackPosition, 3) * 6,
                              x: Math.min(item.stackPosition, 3) * 6,
                              zIndex: previewItems.length - item.stackPosition,
                              rotate: Math.min(item.stackPosition, 3) * 2,
                            }}
                            exit={{ opacity: 0, scale: 0.8, x: -100 }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 25,
                            }}
                            drag={
                              isTopCard && previewItems.length > 1 ? "x" : false
                            }
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.7}
                            onDragStart={() => setIsDragging(true)}
                            onDragEnd={handlePreviewDragEnd}
                            whileDrag={{ scale: 1.05, cursor: "grabbing" }}
                            className={`absolute w-full h-full rounded-xl border border-gray-700 bg-gray-900 overflow-hidden shadow-lg ${
                              isTopCard && previewItems.length > 1
                                ? "cursor-grab active:cursor-grabbing"
                                : ""
                            }`}
                          >
                            <img
                              src={item.image}
                              alt=""
                              className="w-full h-full object-cover pointer-events-none"
                            />
                          </motion.div>
                        );
                      })}
                  </AnimatePresence>
                </div>
              </div>

              {/* Navigation dots - REMOVED */}

              {/* Rename field */}
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-2">
                  Collection Name
                </label>
                <input
                  type="text"
                  value={editCompilationName}
                  onChange={(e) => setEditCompilationName(e.target.value)}
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-400"
                  placeholder="Enter collection name"
                />
              </div>

              {/* Save button */}
              <button
                onClick={handleSaveCompilationChanges}
                disabled={!editCompilationName.trim()}
                className="w-full py-2.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                Save Changes
              </button>

              {/* Delete button */}
              <div className="border-t border-gray-800 pt-4">
                <button
                  onClick={handleDeleteCompilation}
                  className="w-full py-2.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 font-semibold hover:bg-red-500/30 hover:text-red-300 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete Collection
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Deleting this collection will not remove items from favorites.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {isSettingsOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div
              className="w-full max-w-sm md:max-w-md bg-[#1a1a20] rounded-2xl p-6 border border-gray-800 shadow-xl"
              style={{
                background: `linear-gradient(to bottom, #1a1a20, #0f0f0f)`,
              }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Edit Profile</h2>
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editForm.username ? `@${editForm.username}` : ""}
                    readOnly
                    className="w-full bg-gray-900/50 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed focus:outline-none"
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Name is synced from Telegram
                  </p>
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Status
                  </label>
                  <textarea
                    rows={3}
                    value={editForm.status}
                    onChange={(e) =>
                      setEditForm({ ...editForm, status: e.target.value })
                    }
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-400 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Profession
                  </label>
                  <input
                    type="text"
                    value={editForm.profession}
                    onChange={(e) =>
                      setEditForm({ ...editForm, profession: e.target.value })
                    }
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-400"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Tags
                  </label>
                  <div className="flex gap-2 mb-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                        placeholder="Add tag..."
                        className="w-full bg-gray-900/50 border border-gray-700 rounded-lg pl-3 pr-10 py-2 text-sm text-white focus:outline-none focus:border-orange-400"
                      />
                      <button
                        onClick={handleAddTag}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {editForm.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-800 text-xs border border-gray-700"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(index)}
                          className="text-gray-400 hover:text-red-400 ml-1"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSaveSettings}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 rounded-lg mt-4 flex items-center justify-center gap-2 transition-colors"
                >
                  <Check size={16} />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save to Favorites Modal */}
        {bookmarkTorrent && (
          <SaveToFavoritesModal
            isOpen={!!bookmarkTorrent}
            onClose={() => setBookmarkTorrent(null)}
            torrent={bookmarkTorrent}
            onFavoriteChange={handleLocalFavoriteChange}
            onDataChange={handleDataRefresh}
          />
        )}

        <BackToTopButton
          targetElement={scrollTarget}
          isModal={true}
          className="back-to-top-profile"
        />
      </div>
    </div>
  );
}
